if (!customElements.get('adaptable-header')) {
  class AdaptableHeader extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.wrapper = this.closest('.section-header');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.setupAnnouncement();
      this.setupDesktopNavigation();
      this.setupMobileNavigation();
      this.setupResponsiveNavigationState();
      this.setupStickyHeader();
      this.setupHeaderMeasurements();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.stopAnnouncement();
      this.resizeObserver?.disconnect();
      this.desktopHoverTimers?.forEach((timer) => window.clearTimeout(timer));
      this.desktopHoverTimers?.clear();
      this.initialized = false;
    }

    setupAnnouncement() {
      this.announcement = this.querySelector('[data-announcement]');
      if (!this.announcement) return;

      this.track = this.announcement.querySelector('[data-announcement-track]');
      this.slides = Array.from(this.announcement.querySelectorAll('[data-announcement-slide]'));
      this.pauseButton = this.announcement.querySelector('[data-announcement-pause]');
      this.activeSlide = 0;
      this.userPaused = false;
      this.pointerPaused = false;
      this.focusPaused = false;
      this.autoplay = this.announcement.dataset.autoplay === 'true';
      this.interval = Math.max(Number(this.announcement.dataset.speed) || 5, 1) * 1000;

      this.setSlide(0);
      if (this.slides.length < 2) return;

      this.announcement.addEventListener('pointerenter', () => {
        this.pointerPaused = true;
        this.syncAnnouncement();
      }, { signal: this.signal });

      this.announcement.addEventListener('pointerleave', () => {
        this.pointerPaused = false;
        this.syncAnnouncement();
      }, { signal: this.signal });

      this.announcement.addEventListener('focusin', () => {
        this.focusPaused = true;
        this.syncAnnouncement();
      }, { signal: this.signal });

      this.announcement.addEventListener('focusout', (event) => {
        if (event.relatedTarget && this.announcement.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        this.syncAnnouncement();
      }, { signal: this.signal });

      this.pauseButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.updatePauseButton();
        this.syncAnnouncement();
      }, { signal: this.signal });

      document.addEventListener('visibilitychange', () => this.syncAnnouncement(), { signal: this.signal });
      this.reducedMotion.addEventListener('change', () => this.syncAnnouncement(), { signal: this.signal });

      this.addEventListener('shopify:block:select', (event) => {
        const selected = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
        if (selected < 0) return;
        this.userPaused = true;
        this.updatePauseButton();
        this.setSlide(selected);
        this.syncAnnouncement();
      }, { signal: this.signal });

      this.updatePauseButton();
      this.syncAnnouncement();
    }

    setSlide(index) {
      if (!this.slides?.length) return;
      this.activeSlide = (index + this.slides.length) % this.slides.length;
      this.track?.style.setProperty('--ah-slide-index', this.activeSlide);

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.activeSlide;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
          if (active) element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', '-1');
        });
      });
    }

    updatePauseButton() {
      if (!this.pauseButton) return;
      this.pauseButton.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
      this.pauseButton.setAttribute(
        'aria-label',
        this.userPaused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel
      );
    }

    syncAnnouncement() {
      this.stopAnnouncement();
      const shouldRun = this.autoplay
        && this.slides?.length > 1
        && !this.userPaused
        && !this.pointerPaused
        && !this.focusPaused
        && !document.hidden
        && !this.reducedMotion.matches;

      if (!shouldRun) return;
      this.announcementTimer = window.setInterval(() => this.setSlide(this.activeSlide + 1), this.interval);
    }

    stopAnnouncement() {
      window.clearInterval(this.announcementTimer);
      this.announcementTimer = undefined;
    }

    setupDesktopNavigation() {
      this.desktopDetails = Array.from(this.querySelectorAll('[data-desktop-nav-details]'));
      if (!this.desktopDetails.length) return;

      this.desktopHoverTimers = new Map();
      this.desktopHoverEnabled = this.dataset.desktopMenuBehavior === 'hover';
      this.desktopHoverMedia = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');

      this.desktopDetails.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (details.open) this.openDesktopDetails(details);
        }, { signal: this.signal });

        if (this.desktopHoverEnabled) {
          details.addEventListener('pointerenter', () => {
            if (!this.desktopHoverMedia.matches) return;
            this.clearDesktopHoverTimer(details);
            this.openDesktopDetails(details);
          }, { signal: this.signal });

          details.addEventListener('pointerleave', () => {
            if (!this.desktopHoverMedia.matches) return;
            this.clearDesktopHoverTimer(details);
            const timer = window.setTimeout(() => {
              this.desktopHoverTimers.delete(details);
              if (!details.contains(document.activeElement)) details.removeAttribute('open');
            }, 120);
            this.desktopHoverTimers.set(details, timer);
          }, { signal: this.signal });

          details.addEventListener('focusin', () => this.clearDesktopHoverTimer(details), { signal: this.signal });
        }
      });

      this.setupMegaGalleryTabs();

      document.addEventListener('click', (event) => {
        const openDetails = this.desktopDetails.find((details) => details.open);
        if (!openDetails || openDetails.contains(event.target)) return;
        this.closeDesktopNavigation();
      }, { signal: this.signal });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const openDetails = this.desktopDetails.find((details) => details.open);
        if (!openDetails) return;
        const summary = openDetails.querySelector('summary');
        openDetails.removeAttribute('open');
        summary?.focus();
      }, { signal: this.signal });

      this.addEventListener('shopify:block:select', (event) => {
        const blockId = event.detail?.blockId;
        if (!blockId) return;

        if (window.matchMedia('(max-width: 989px)').matches) {
          this.openMobileMegaBlock(blockId);
          return;
        }

        const details = this.desktopDetails.find(
          (item) => item.hasAttribute('data-ah-mega-details') && item.dataset.megaBlockId === blockId
        );
        if (!details) return;

        this.openDesktopDetails(details);
        const megaMenu = details.querySelector('[data-ah-mega]');
        const selectedTab = megaMenu?.querySelector('[data-ah-mega-tab][aria-selected="true"]')
          || megaMenu?.querySelector('[data-ah-mega-tab]');
        if (megaMenu && selectedTab) this.activateMegaTab(megaMenu, selectedTab);
      }, { signal: this.signal });
    }

    openMobileMegaBlock(blockId) {
      const panel = Array.from(this.querySelectorAll('[data-ah-mobile-panel][data-ah-mega-block-id]'))
        .find((item) => item.dataset.ahMegaBlockId === blockId);
      if (!panel) return;

      const drawerDetails = this.querySelector('.ah-drawer > details');
      const drawerSummary = drawerDetails?.querySelector(':scope > summary');
      const drilldownDetails = panel.closest('.ah-drawer__drilldown-details');
      const drilldownSummary = drilldownDetails?.querySelector(':scope > summary');
      if (!drawerDetails || !drawerSummary || !drilldownDetails || !drilldownSummary) return;

      if (!drawerDetails.open) drawerSummary.click();

      window.setTimeout(() => {
        if (!drilldownDetails.open) drilldownSummary.click();
        window.setTimeout(() => panel.scrollTo({ top: 0, behavior: 'auto' }), 120);
      }, 120);
    }

    setupMegaGalleryTabs() {
      this.querySelectorAll('[data-ah-mega]').forEach((megaMenu) => {
        const tabs = Array.from(megaMenu.querySelectorAll('[data-ah-mega-tab]'));
        if (!tabs.length) return;

        const selectedTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
        this.activateMegaTab(megaMenu, selectedTab);

        tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.activateMegaTab(megaMenu, tab), { signal: this.signal });
          tab.addEventListener('keydown', (event) => this.onMegaTabKeydown(event, megaMenu, tabs), {
            signal: this.signal,
          });
        });
      });
    }

    activateMegaTab(megaMenu, selectedTab, focus = false) {
      const tabs = Array.from(megaMenu.querySelectorAll('[data-ah-mega-tab]'));
      const panels = Array.from(megaMenu.querySelectorAll('[data-ah-mega-panel]'));
      if (!tabs.includes(selectedTab)) return;

      const selectedPanelId = selectedTab.dataset.ahMegaPanelId || selectedTab.getAttribute('aria-controls');
      if (!selectedPanelId || !panels.some((panel) => panel.id === selectedPanelId)) return;

      tabs.forEach((tab) => {
        const active = tab === selectedTab;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
        tab.classList.toggle('is-active', active);
      });

      panels.forEach((panel) => {
        const active = panel.id === selectedPanelId;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      if (focus) selectedTab.focus();
    }

    onMegaTabKeydown(event, megaMenu, tabs) {
      const currentIndex = tabs.indexOf(event.currentTarget);
      if (currentIndex < 0) return;

      let nextIndex;
      const tablist = event.currentTarget.closest('[role="tablist"]');
      const isRtl = tablist
        ? window.getComputedStyle(tablist).direction === 'rtl'
        : document.documentElement.dir === 'rtl';

      switch (event.key) {
        case 'ArrowRight':
          nextIndex = (currentIndex + (isRtl ? -1 : 1) + tabs.length) % tabs.length;
          break;
        case 'ArrowLeft':
          nextIndex = (currentIndex + (isRtl ? 1 : -1) + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.activateMegaTab(megaMenu, tabs[nextIndex], true);
    }

    openDesktopDetails(details) {
      this.clearDesktopHoverTimer(details);
      this.desktopDetails?.forEach((other) => {
        if (other !== details) {
          this.clearDesktopHoverTimer(other);
          other.removeAttribute('open');
        }
      });
      details.setAttribute('open', '');
    }

    clearDesktopHoverTimer(details) {
      const timer = this.desktopHoverTimers?.get(details);
      if (timer === undefined) return;
      window.clearTimeout(timer);
      this.desktopHoverTimers.delete(details);
    }

    closeDesktopNavigation() {
      this.desktopDetails?.forEach((details) => {
        this.clearDesktopHoverTimer(details);
        details.removeAttribute('open');
      });
    }

    setupMobileNavigation() {
      this.mobileAccordionTriggers = Array.from(this.querySelectorAll('[data-ah-mobile-accordion]'));
      if (!this.mobileAccordionTriggers.length) return;

      this.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        const trigger = event.target.closest('[data-ah-mobile-accordion]');
        if (!trigger || !this.contains(trigger)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.toggleMobileAccordion(trigger);
      }, { capture: true, signal: this.signal });

      this.querySelectorAll('.ah-drawer__drilldown-details').forEach((details) => {
        details.addEventListener('toggle', () => {
          if (!details.open) this.resetMobileAccordions(details);
        }, { signal: this.signal });
      });

      const mainDrawerDetails = this.querySelector('.ah-drawer > details');
      mainDrawerDetails?.addEventListener('toggle', () => {
        if (!mainDrawerDetails.open) this.resetMobileAccordions(mainDrawerDetails);
      }, { signal: this.signal });
    }

    toggleMobileAccordion(trigger) {
      const group = trigger.closest('[data-ah-mobile-accordion-group]');
      if (!group) return;

      const willOpen = trigger.getAttribute('aria-expanded') !== 'true';
      group.querySelectorAll('[data-ah-mobile-accordion]').forEach((item) => {
        this.setMobileAccordionState(item, item === trigger && willOpen);
      });
    }

    setMobileAccordionState(trigger, expanded) {
      const panelId = trigger.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (!panel || !this.contains(panel)) return;

      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      panel.hidden = !expanded;
    }

    resetMobileAccordions(container = this) {
      container.querySelectorAll('[data-ah-mobile-accordion]').forEach((trigger) => {
        this.setMobileAccordionState(trigger, false);
      });
    }

    setupResponsiveNavigationState() {
      this.mobileNavigationMedia = window.matchMedia('(max-width: 989px)');
      this.mobileNavigationMedia.addEventListener('change', (event) => {
        this.handleNavigationBreakpointChange(event);
      }, { signal: this.signal });
    }

    handleNavigationBreakpointChange(event) {
      if (event.matches) {
        const focusedDesktopDetails = this.desktopDetails?.find((details) => details.contains(document.activeElement));
        this.closeDesktopNavigation();
        if (!focusedDesktopDetails) return;

        window.requestAnimationFrame(() => {
          this.querySelector('.ah-drawer > details > summary')?.focus();
        });
        return;
      }

      this.closeMobileNavigationForDesktop();
    }

    closeMobileNavigationForDesktop() {
      const drawer = this.querySelector('header-drawer.ah-drawer');
      const mainDetails = drawer?.querySelector(':scope > details');
      if (!drawer || !mainDetails) return;

      const overflowClass = `overflow-hidden-${drawer.dataset.breakpoint}`;
      const drawerWasActive = mainDetails.open
        || mainDetails.classList.contains('menu-opening')
        || Boolean(mainDetails.querySelector('.menu-opening, .ah-drawer__drilldown-details[open]'))
        || document.body.classList.contains(overflowClass);
      if (!drawerWasActive) return;

      const summary = mainDetails.querySelector(':scope > summary');
      const drilldowns = Array.from(mainDetails.querySelectorAll('.ah-drawer__drilldown-details'));
      const openDrilldownIndex = drilldowns.findIndex((details) => details.open);
      const desktopFocusTarget = openDrilldownIndex >= 0
        ? this.desktopDetails?.[openDrilldownIndex]?.querySelector('summary')
        : this.querySelector('.ah-logo');
      const drawerHadFocus = mainDetails.contains(document.activeElement);

      mainDetails.classList.remove('menu-opening');
      mainDetails.querySelectorAll('details').forEach((details) => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
        details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
      });
      mainDetails.querySelectorAll('.submenu-open').forEach((submenu) => submenu.classList.remove('submenu-open'));
      mainDetails.removeAttribute('open');
      summary?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove(overflowClass);
      this.wrapper?.classList.remove('menu-open');
      this.resetMobileAccordions(mainDetails);

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(drawerHadFocus ? desktopFocusTarget : false);
      }
      if (drawerHadFocus && desktopFocusTarget && document.activeElement !== desktopFocusTarget) {
        desktopFocusTarget.focus();
      }
    }

    setupStickyHeader() {
      if (!this.wrapper || this.dataset.sticky !== 'on-scroll-up') return;
      this.lastScrollTop = window.scrollY;
      this.scrollTicking = false;

      window.addEventListener('scroll', () => {
        if (this.scrollTicking) return;
        this.scrollTicking = true;
        window.requestAnimationFrame(() => {
          const currentScrollTop = Math.max(window.scrollY, 0);
          const movingDown = currentScrollTop > this.lastScrollTop;
          const beyondHeader = currentScrollTop > this.wrapper.offsetHeight;
          const menuOpen = this.wrapper.classList.contains('menu-open');
          this.wrapper.classList.toggle('ah-sticky-hidden', movingDown && beyondHeader && !menuOpen);
          this.lastScrollTop = currentScrollTop;
          this.scrollTicking = false;
        });
      }, { passive: true, signal: this.signal });
    }

    setupHeaderMeasurements() {
      if (!this.wrapper || !('ResizeObserver' in window)) return;
      const update = () => {
        document.documentElement.style.setProperty('--header-height', `${this.wrapper.offsetHeight}px`);
      };
      this.resizeObserver = new ResizeObserver(update);
      this.resizeObserver.observe(this.wrapper);
      update();
    }
  }

  customElements.define('adaptable-header', AdaptableHeader);
}
